import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Building2, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal } from "../components/site/Motion";
import { SectionLabel } from "../components/site/Bits";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { SUPPORT_EMAIL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Message sent!");
      setSent(true);
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-background pt-28">
      <section className="relative overflow-hidden">
        <div className="mesh-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <Reveal className="max-w-2xl">
            <SectionLabel>Contact</SectionLabel>
            <h1 className="mt-5 font-display text-5xl font-extrabold leading-[1.02] tracking-tighter text-ink sm:text-6xl">
              Let's talk renewals.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Questions about RenewTracker for your business, or want early access at launch? Send us a message — we'd love to hear from you.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
            {/* Form */}
            <div className="lg:col-span-7">
              <Reveal>
                <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm sm:p-10">
                  {sent ? (
                    <div className="flex flex-col items-center py-10 text-center" data-testid="contact-success">
                      <span className="grid h-16 w-16 place-items-center rounded-full bg-greenaccent/15">
                        <CheckCircle2 className="h-8 w-8 text-greenaccent" />
                      </span>
                      <h3 className="mt-5 font-display text-2xl font-semibold text-ink">Message received</h3>
                      <p className="mt-2 max-w-sm text-sm text-slate-600">
                        Thanks for reaching out. Our team will get back to you at the email you provided shortly.
                      </p>
                      <button
                        onClick={() => setSent(false)}
                        className="mt-6 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-brand hover:text-brand"
                        data-testid="send-another-btn"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full name *</Label>
                          <Input id="name" data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Jane Doe" className="h-12 rounded-xl border-slate-200 focus-visible:ring-brand" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input id="email" type="email" data-testid="contact-email-input" value={form.email} onChange={update("email")} placeholder="you@company.com" className="h-12 rounded-xl border-slate-200 focus-visible:ring-brand" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="company">Company (optional)</Label>
                          <Input id="company" data-testid="contact-company" value={form.company} onChange={update("company")} placeholder="Acme Ltd" className="h-12 rounded-xl border-slate-200 focus-visible:ring-brand" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">Subject (optional)</Label>
                          <Input id="subject" data-testid="contact-subject" value={form.subject} onChange={update("subject")} placeholder="Early access enquiry" className="h-12 rounded-xl border-slate-200 focus-visible:ring-brand" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea id="message" data-testid="contact-message" value={form.message} onChange={update("message")} placeholder="Tell us how we can help…" rows={5} className="rounded-xl border-slate-200 focus-visible:ring-brand" />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        data-testid="contact-submit-btn"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-brand-dark disabled:opacity-60 sm:w-auto"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {loading ? "Sending…" : "Send message"}
                      </button>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>

            {/* Business contact */}
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="space-y-4">
                  <div className="rounded-3xl border border-slate-200 bg-white p-7">
                    <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand/5 text-brand">
                      <Mail className="h-5 w-5" />
                    </span>
                    <h3 className="mt-4 font-display text-lg font-semibold text-ink">Email support</h3>
                    <p className="mt-1 text-sm text-slate-600">We typically reply within one business day.</p>
                    <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="contact-support-email" className="mt-3 inline-block text-sm font-semibold text-brand hover:underline">
                      {SUPPORT_EMAIL}
                    </a>
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-white p-7">
                    <span className="grid h-11 w-11 place-items-center rounded-2xl bg-greenaccent/10 text-greenaccent">
                      <Building2 className="h-5 w-5" />
                    </span>
                    <h3 className="mt-4 font-display text-lg font-semibold text-ink">Business & partnerships</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      Interested in RenewTracker for your organisation or a partnership? Mention it in your message and our team will be in touch.
                    </p>
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-ink p-7 text-white">
                    <span className="grid h-11 w-11 place-items-center rounded-2xl bg-white/10 text-greenaccent">
                      <MapPin className="h-5 w-5" />
                    </span>
                    <h3 className="mt-4 font-display text-lg font-semibold">Online first</h3>
                    <p className="mt-1 text-sm leading-relaxed text-white/60">
                      RenewTracker is a digital-first product available wherever you and your team work. Reach us anytime at{" "}
                      <a href="https://renewtracker.site" className="text-greenaccent hover:underline">renewtracker.site</a>.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
